import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Empty,
  Input,
  message,
  Modal,
  Popconfirm,
  Result,
  Segmented,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  Descriptions,
  Image,
  Divider
} from 'antd'
import {
  FileDoneOutlined,
  ReloadOutlined,
  SearchOutlined,
  SendOutlined,
  StopOutlined,
  PrinterOutlined,
  LinkOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import ConsentSignLinkModal from '../components/ConsentSignLinkModal'
import dayjs from 'dayjs'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { getApiErrorMessage } from '../utils/api'
import {
  listConsentForms,
  getConsentFormDetail,
  resendConsentForm,
  getConsentSignLink,
  cancelConsentForm,
  consentFormPrintableUrl,
  CONSENT_STATUS_META
} from '../api/consentForm'
import { API_BASE_URL } from '../utils/apiutils'
import { formatTz } from '../utils/timezone'

const { Title, Text, Paragraph } = Typography

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 24px;
  background: #f5f5f5;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HeaderCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid #eef0f5;
  box-shadow: 0 6px 18px rgba(15, 37, 98, 0.05);

  .ant-card-body {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
  }
`

const TableCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid #eef0f5;
  box-shadow: 0 6px 18px rgba(15, 37, 98, 0.05);
`

const ToolBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;

    .ant-segmented {
      overflow-x: auto;
    }
  }
`

/* ===== 手機版卡片列表 ===== */
const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const RecordCard = styled.div`
  background: #fff;
  border: 1px solid #eef0f5;
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 2px 10px rgba(15, 37, 98, 0.06);
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.1s ease;

  &:active {
    transform: scale(0.99);
    box-shadow: 0 1px 4px rgba(15, 37, 98, 0.08);
  }
`

const CardTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`

const CardTitle = styled.div`
  flex: 1;
  font-size: 15px;
  font-weight: 700;
  color: #1f2d3d;
  line-height: 1.4;
`

const CardSub = styled.div`
  font-size: 12px;
  color: #8c96a5;
  margin-top: 2px;
`

const CardCustomerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 8px 10px;
  background: #f7f9fc;
  border-radius: 10px;
  font-size: 13px;
  color: #2c3e50;
`

const CardTimesRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 10px;
  font-size: 12px;
  color: #8c96a5;

  span b {
    color: #5a6675;
    font-weight: 500;
  }
`

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;

  .ant-btn {
    flex: 1;
    border-radius: 10px;
    height: 36px;
  }
`

const CONSENT_COLOR = '#5b8def'

const statusTag = (status) => {
  const meta = CONSENT_STATUS_META[status] || { color: 'default', label: status }
  return <Tag color={meta.color}>{meta.label}</Tag>
}

const ConsentFormRecordsPage = () => {
  const { user } = useAuth()
  const { hasFeature, permissionsLoaded } = useMembership()
  const businessId = user?.businessId

  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [status, setStatus] = useState('all')
  const [keyword, setKeyword] = useState('')
  // 手機版偵測（≤768px 以卡片列表呈現，取代表格）
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  )
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const [detailOpen, setDetailOpen] = useState(false)
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const featureEnabled = hasFeature('consentForm')

  const fetchRows = useCallback(async () => {
    if (!businessId) return
    setLoading(true)
    try {
      const res = await listConsentForms(businessId, {
        status,
        keyword,
        pageSize: 100
      })
      setRows(res?.data?.data || [])
    } catch (err) {
      message.error(getApiErrorMessage(err, '取得簽署紀錄失敗'))
    } finally {
      setLoading(false)
    }
  }, [businessId, status, keyword])

  useEffect(() => {
    if (featureEnabled) fetchRows()
  }, [fetchRows, featureEnabled])

  const openDetail = async (row) => {
    setDetail(row)
    setDetailOpen(true)
    setDetailLoading(true)
    try {
      const res = await getConsentFormDetail(businessId, row._id)
      setDetail(res?.data?.data || row)
    } catch (err) {
      message.error(getApiErrorMessage(err, '取得詳情失敗'))
    } finally {
      setDetailLoading(false)
    }
  }

  // 免登入簽署連結視窗：重發沒送到時自動跳出，也可以從「連結」按鈕打開
  const [signLink, setSignLink] = useState({
    open: false,
    url: '',
    undelivered: false
  })

  const handleResend = async (row) => {
    try {
      const res = await resendConsentForm(businessId, row._id)
      const body = res?.data || {}
      // 後端照實回報有沒有送到；沒送到就直接給連結，不要讓店家以為發出去了
      if (body.delivered === false && body.signUrl) {
        setSignLink({ open: true, url: body.signUrl, undelivered: true })
      } else {
        message.success(body.message || '已重新發送')
      }
      fetchRows()
    } catch (err) {
      message.error(getApiErrorMessage(err, '重新發送失敗'))
    }
  }

  const openSignLink = async (row) => {
    try {
      const res = await getConsentSignLink(businessId, row._id)
      const url = res?.data?.data?.url
      if (!url) throw new Error('no url')
      setSignLink({ open: true, url, undelivered: false })
    } catch (err) {
      message.error(getApiErrorMessage(err, '取得簽署連結失敗'))
    }
  }

  const handleCancel = async (row) => {
    try {
      await cancelConsentForm(businessId, row._id)
      message.success('已取消')
      fetchRows()
      if (detailOpen) setDetailOpen(false)
    } catch (err) {
      message.error(getApiErrorMessage(err, '取消失敗'))
    }
  }

  const openPrintable = (row) => {
    const url = `${API_BASE_URL}${consentFormPrintableUrl(businessId, row._id)}`
    window.open(url, '_blank')
  }

  if (!permissionsLoaded) {
    return (
      <PageWrapper>
        <Spin />
      </PageWrapper>
    )
  }
  if (!featureEnabled) {
    return (
      <PageWrapper>
        <Result
          status='warning'
          title='您的方案尚未開通同意書模組'
          subTitle='請升級至高階方案以使用項目施作同意書功能。'
        />
      </PageWrapper>
    )
  }

  const columns = [
    {
      title: '同意書',
      dataIndex: 'templateTitle',
      key: 'templateTitle',
      render: (v, r) => (
        <div>
          <Text strong>{v}</Text>
          {r.serviceName ? (
            <div style={{ fontSize: 12, color: '#888' }}>{r.serviceName}</div>
          ) : null}
        </div>
      )
    },
    {
      title: '客戶',
      key: 'customer',
      width: 160,
      render: (_, r) => (
        <div>
          <div>{r.customerName}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{r.customerPhone}</div>
        </div>
      )
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v) => statusTag(v)
    },
    {
      title: '發送時間',
      dataIndex: 'sentAt',
      key: 'sentAt',
      width: 150,
      render: (v) => (v ? dayjs(v).format('YYYY/MM/DD HH:mm') : '—')
    },
    {
      title: '簽署時間',
      dataIndex: 'signedAt',
      key: 'signedAt',
      width: 150,
      render: (v) => (v ? dayjs(v).format('YYYY/MM/DD HH:mm') : '—')
    },
    {
      title: '操作',
      key: 'actions',
      width: 220,
      render: (_, r) => (
        <Space size={4} wrap>
          <Button size='small' onClick={() => openDetail(r)}>
            檢視
          </Button>
          {r.status === 'signed' && (
            <Button
              size='small'
              icon={<PrinterOutlined />}
              onClick={() => openPrintable(r)}
            >
              留存
            </Button>
          )}
          {r.status !== 'signed' && r.status !== 'canceled' && (
            <Button
              size='small'
              icon={<LinkOutlined />}
              onClick={() => openSignLink(r)}
            >
              連結
            </Button>
          )}
          {r.status !== 'signed' && r.status !== 'canceled' && (
            <Button
              size='small'
              icon={<SendOutlined />}
              onClick={() => handleResend(r)}
            >
              重發
            </Button>
          )}
          {r.status !== 'signed' && r.status !== 'canceled' && (
            <Popconfirm
              title='取消此同意書？'
              okText='取消發送'
              cancelText='返回'
              onConfirm={() => handleCancel(r)}
            >
              <Button size='small' danger icon={<StopOutlined />}>
                取消
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ]

  const sig = detail?.signature

  return (
    <PageWrapper>
      <ContentWrapper>
        <HeaderCard>
          <Space size='middle' align='center'>
            <FileDoneOutlined style={{ fontSize: 22, color: CONSENT_COLOR }} />
            <Title level={4} style={{ margin: 0 }}>
              簽署紀錄
            </Title>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={fetchRows}>
            重新整理
          </Button>
        </HeaderCard>

        <TableCard>
          <ToolBar>
            <Segmented
              value={status}
              onChange={setStatus}
              options={[
                { label: '全部', value: 'all' },
                { label: '待簽署', value: 'pending' },
                { label: '已簽署', value: 'signed' },
                { label: '已逾期', value: 'expired' },
                { label: '已取消', value: 'canceled' }
              ]}
            />
            <Input
              allowClear
              placeholder='客戶姓名 / 電話 / 同意書名稱'
              prefix={<SearchOutlined />}
              style={{ width: isMobile ? '100%' : 280 }}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onPressEnter={fetchRows}
            />
          </ToolBar>

          {isMobile ? (
            /* 手機版：卡片列表（點卡片開詳情） */
            loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Spin />
              </div>
            ) : rows.length === 0 ? (
              <Empty description='尚無簽署紀錄' />
            ) : (
              <CardList>
                {rows.map((r) => (
                  <RecordCard key={r._id} onClick={() => openDetail(r)}>
                    <CardTopRow>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <CardTitle>{r.templateTitle}</CardTitle>
                        {r.serviceName ? <CardSub>{r.serviceName}</CardSub> : null}
                      </div>
                      {statusTag(r.status)}
                    </CardTopRow>

                    <CardCustomerRow>
                      <span style={{ fontWeight: 600 }}>{r.customerName}</span>
                      {r.customerPhone ? (
                        <span style={{ color: '#8c96a5' }}>{r.customerPhone}</span>
                      ) : null}
                    </CardCustomerRow>

                    <CardTimesRow>
                      <span>
                        <b>發送</b>{' '}
                        {formatTz(r.sentAt, 'MM/DD HH:mm', '—')}
                      </span>
                      <span>
                        <b>簽署</b>{' '}
                        {formatTz(r.signedAt, 'MM/DD HH:mm', '—')}
                      </span>
                    </CardTimesRow>

                    <CardActions onClick={(e) => e.stopPropagation()}>
                      <Button onClick={() => openDetail(r)}>檢視</Button>
                      {r.status === 'signed' && (
                        <Button
                          icon={<PrinterOutlined />}
                          onClick={() => openPrintable(r)}
                        >
                          留存
                        </Button>
                      )}
                      {r.status !== 'signed' && r.status !== 'canceled' && (
                        <Button
                          icon={<LinkOutlined />}
                          onClick={() => openSignLink(r)}
                        >
                          簽署連結
                        </Button>
                      )}
                      {r.status !== 'signed' && r.status !== 'canceled' && (
                        <Button
                          type='primary'
                          ghost
                          icon={<SendOutlined />}
                          onClick={() => handleResend(r)}
                        >
                          重發
                        </Button>
                      )}
                      {r.status !== 'signed' && r.status !== 'canceled' && (
                        <Popconfirm
                          title='取消此同意書？'
                          okText='取消發送'
                          cancelText='返回'
                          onConfirm={() => handleCancel(r)}
                        >
                          <Button danger icon={<StopOutlined />}>
                            取消
                          </Button>
                        </Popconfirm>
                      )}
                    </CardActions>
                  </RecordCard>
                ))}
              </CardList>
            )
          ) : (
            <Table
              rowKey='_id'
              columns={columns}
              dataSource={rows}
              loading={loading}
              scroll={{ x: 900 }}
              locale={{ emptyText: <Empty description='尚無簽署紀錄' /> }}
              pagination={{ pageSize: 20, showSizeChanger: false }}
            />
          )}
        </TableCard>
      </ContentWrapper>

      <Modal
        open={detailOpen}
        title='同意書詳情'
        footer={
          detail?.status === 'signed' ? (
            <Button
              type='primary'
              icon={<PrinterOutlined />}
              onClick={() => detail && openPrintable(detail)}
            >
              開啟留存檔（可另存 PDF）
            </Button>
          ) : null
        }
        onCancel={() => setDetailOpen(false)}
        width={isMobile ? '94%' : 680}
      >
        {detailLoading ? (
          <Spin />
        ) : detail ? (
          <>
            <Descriptions column={1} size='small' bordered>
              <Descriptions.Item label='同意書'>
                {detail.templateTitle}（v{detail.templateVersion}）
              </Descriptions.Item>
              <Descriptions.Item label='客戶'>
                {detail.customerName}　{detail.customerPhone}
              </Descriptions.Item>
              <Descriptions.Item label='服務項目'>
                {detail.serviceName || '—'}
              </Descriptions.Item>
              <Descriptions.Item label='狀態'>
                {statusTag(detail.status)}
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation='left' plain>
              條款內容
            </Divider>
            <Paragraph
              style={{
                whiteSpace: 'pre-wrap',
                maxHeight: 200,
                overflowY: 'auto',
                background: '#fafafa',
                padding: 12,
                borderRadius: 8
              }}
            >
              {detail.contentSnapshot}
            </Paragraph>

            {sig ? (
              <>
                <Divider orientation='left' plain>
                  簽署結果
                </Divider>
                <Descriptions column={1} size='small'>
                  <Descriptions.Item label='簽署人'>
                    {sig.signerName}
                  </Descriptions.Item>
                  {sig.signerIdNumber ? (
                    <Descriptions.Item label='身分證'>
                      {sig.signerIdNumber}
                    </Descriptions.Item>
                  ) : null}
                  <Descriptions.Item label='簽署時間'>
                    {sig.signedAt ? dayjs(sig.signedAt).format('YYYY/MM/DD HH:mm:ss') : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label='來源 IP'>
                    {sig.ipAddress || '—'}
                  </Descriptions.Item>
                </Descriptions>
                {(sig.checkedItems || []).map((c, i) => (
                  <div key={i}>
                    {c.checked ? '☑' : '☐'} {c.label}
                  </div>
                ))}
                {(sig.answers || []).length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <Text type='secondary'>問卷作答：</Text>
                    <Descriptions column={1} size='small' bordered style={{ marginTop: 6 }}>
                      {sig.answers.map((a) => (
                        <Descriptions.Item key={a.key} label={a.label}>
                          {a.type === 'text'
                            ? a.textValue || '—'
                            : (a.selectedLabels || []).join('、') || '—'}
                        </Descriptions.Item>
                      ))}
                    </Descriptions>
                  </div>
                )}
                {sig.signatureImage ? (
                  <div style={{ marginTop: 12 }}>
                    <Text type='secondary'>手寫簽名：</Text>
                    <div>
                      <Image
                        src={sig.signatureImage}
                        alt='signature'
                        style={{ maxWidth: 280, border: '1px solid #eee' }}
                      />
                    </div>
                  </div>
                ) : (
                  <Text type='secondary'>（勾選同意，無手寫簽名）</Text>
                )}
              </>
            ) : (
              <Text type='secondary'>尚未簽署</Text>
            )}
          </>
        ) : null}
      </Modal>
      <ConsentSignLinkModal
        open={signLink.open}
        url={signLink.url}
        undelivered={signLink.undelivered}
        onClose={() => setSignLink((v) => ({ ...v, open: false }))}
      />
    </PageWrapper>
  )
}

export default ConsentFormRecordsPage
